import { Alert, Flex, Form, Input, InputNumber, Radio, Select } from 'antd';
import { Button } from '@/components/button/button';
import { VoiceField } from '@/components/voice-field/voice-field';
import { useNavigate, useOutletContext } from 'react-router';
import { useCreateAgent, usePrefillInstructions } from '@/hooks/use-agents/use-agents';
import { useCompanyPhoneNumbers } from '@/hooks/use-phone-numbers/use-phone-numbers';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageOptions } from '../agents.constants';
import styles from './create-agent.module.scss';

type AgentsOutletContext = { companyId: number | undefined };

export function CreateAgent() {
  const { t } = useTranslation(['agents', 'common']);
  const languageOptions = useLanguageOptions();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { companyId } = useOutletContext<AgentsOutletContext>();
  const { create, isPending } = useCreateAgent(companyId);
  const { phoneNumbers } = useCompanyPhoneNumbers(companyId);
  const { prefill } = usePrefillInstructions(companyId);
  const prefillApplied = useRef(false);

  useEffect(() => {
    if (prefill && !prefillApplied.current) {
      prefillApplied.current = true;
      const updates: Record<string, string> = {};
      if (prefill.name) updates.name = prefill.name;
      if (prefill.system_prompt) updates.system_prompt = prefill.system_prompt;
      if (prefill.first_message) updates.first_message = prefill.first_message;
      if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
      }
    }
  }, [prefill, form]);

  const nameValue = Form.useWatch('name', form);
  const isCreateDisabled = isPending || !String(nameValue || '').trim();

  const phoneOptions =
    phoneNumbers?.map((num) => ({
      label: num.friendly_name || num.phone_number,
      value: num.id,
    })) || [];

  const handleFinish = (values: Record<string, unknown>) => {
    create(
      {
        name: values.name as string,
        system_prompt: values.system_prompt as string | undefined,
        first_message: values.first_message as string | undefined,
        language: values.language as string | undefined,
        voice_id: values.voice_id as string | undefined,
        twilio_phone_number_id: values.twilio_phone_number_id as number | undefined,
        monthly_budget_minutes: values.monthly_budget_minutes as number | undefined,
        budget_alert_threshold: values.budget_alert_threshold as number | undefined,
        budget_action: values.budget_action as string | undefined,
      },
      {
        onSuccess: (data) => {
          navigate(`/agents/${data.conversational_agent.id}`);
        },
      }
    );
  };

  return (
    <Flex vertical gap={16} style={{ width: '100%', maxWidth: 720 }}>
      <h2>{t('agents:createAgent')}</h2>
      {prefill?.system_prompt && (
        <Alert
          message={t('agents:create.prefill.title')}
          description={t('agents:create.prefill.description')}
          type="info"
          showIcon
          closable
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className={styles.form}
        initialValues={{
          language: 'en',
          // Input for the LLM, not UI copy: the default agent language is `en`, so it stays English.
          system_prompt:
            'You are a helpful Customer Support Representative. Your job is to help the caller based on the information that is available to you',
          budget_alert_threshold: 80,
          budget_action: 'alert',
        }}
      >
        <Form.Item
          label={t('agents:labels.name')}
          name="name"
          rules={[{ required: true, message: t('agents:validation.nameRequired') }]}
        >
          <Input placeholder={t('agents:placeholders.name')} className={styles.formInput} />
        </Form.Item>

        <Form.Item label={t('agents:labels.systemPrompt')} name="system_prompt">
          <Input.TextArea
            className={styles.textareaInput}
            rows={4}
            autoSize={{ minRows: 4, maxRows: 20 }}
            placeholder={t('agents:placeholders.systemPrompt')}
          />
        </Form.Item>

        <Form.Item label={t('agents:labels.firstMessage')} name="first_message">
          <Input
            className={styles.formInput}
            placeholder={t('agents:placeholders.firstMessage')}
          />
        </Form.Item>

        <Form.Item label={t('agents:labels.language')} name="language">
          <Select options={languageOptions} style={{ width: 200 }} />
        </Form.Item>

        <Form.Item label={t('agents:labels.voice')} name="voice_id">
          <VoiceField companyId={companyId} />
        </Form.Item>

        <Form.Item label={t('agents:labels.phoneNumber')} name="twilio_phone_number_id">
          <Select
            placeholder={t('agents:placeholders.phoneNumber')}
            options={phoneOptions}
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label={t('agents:labels.monthlyBudget')} name="monthly_budget_minutes">
          <InputNumber min={0} style={{ width: 200 }} placeholder={t('agents:placeholders.noLimit')} />
        </Form.Item>

        <Form.Item label={t('agents:labels.budgetAlertThreshold')} name="budget_alert_threshold">
          <InputNumber min={0} max={100} style={{ width: 200 }} />
        </Form.Item>

        <Form.Item label={t('agents:labels.budgetAction')} name="budget_action">
          <Radio.Group>
            <Radio value="alert">{t('agents:budgetActions.alert')}</Radio>
            <Radio value="auto_unpublish">{t('agents:budgetActions.autoUnpublish')}</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>

      <Flex gap={8} justify="end">
        <Button buttonVariant="secondary" onClick={() => navigate('/agents')}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          buttonVariant="primary"
          loading={isPending}
          disabled={isCreateDisabled}
          onClick={() => form.submit()}
        >
          {t('agents:createAgent')}
        </Button>
      </Flex>
    </Flex>
  );
}
